import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// PM / "Book" data layer. Every PM RPC's first identity arg is the signed-in user's OWN
// uuid — never someone else's. Admins (Sam/Mark) get the whole firm book. No trade sizing
// anywhere (the backend omits it; so does the UI).

type Row = Record<string, any>;

async function ownUid(): Promise<string> {
  const { data } = await supabase.auth.getUser();
  const uid = data.user?.id;
  if (!uid) throw new Error("not_signed_in");
  return uid;
}

// The three fully-modelled ("bridged") scenarios with real loss numbers.
export const BRIDGED_SCENARIOS = [
  "Global Financial Crisis",
  "COVID-19 Shock",
  "2022–2023 Inflation Spike & Yields Repricing",
] as const;

export interface BookImpact extends Row {
  resolved_scenario?: { label?: string } & Row;
  scenario_bridged?: boolean;
  mandate_fund_count?: number;
  book_position_count?: number;
  book_positions?: Row[];
  most_exposed?: Row[];
  quantified_impact?: Row[];
  effective_dates?: Row;
  notes?: string;
  error?: string; // e.g. 'scenario_not_resolved'
}
export function usePmBookImpact(scenario: string | undefined) {
  return useQuery({
    queryKey: ["research_pm_book_impact", scenario],
    enabled: !!scenario,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const uid = await ownUid();
      const { data, error } = await supabase.rpc("research_pm_book_impact", {
        _user_id: uid,
        _scenario: scenario as string,
      });
      if (error) throw error;
      return (data ?? {}) as unknown as BookImpact;
    },
  });
}

export interface ExposureDriverRow extends Row {
  driver_label?: string;
  sign?: string | number;
  mean_propagation_score?: number;
  reached_fund_count?: number;
  exemplar_path_labels?: string[];
  conflict_flag?: boolean;
}
export function usePmExposure() {
  return useQuery({
    queryKey: ["research_pm_exposure_attribution"],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const uid = await ownUid();
      const { data, error } = await supabase.rpc("research_pm_exposure_attribution", {
        _user_id: uid,
        _scenarios: null,
      });
      if (error) throw error;
      return ((data as unknown as ExposureDriverRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface PositioningRow extends Row {
  subject_label?: string;
  thesis_edge_id?: string;
  signed_conviction?: number;
  conviction_score?: number;
  action_hint?: string;
  single_source_risk?: boolean;
  reached_fund_count?: number;
  top_disconfirmer?: Row;
  disconfirmer_strength?: number;
  open_divergence_alert_ids?: string[];
}
export function usePmPositioning() {
  return useQuery({
    queryKey: ["research_pm_positioning"],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const uid = await ownUid();
      const { data, error } = await supabase.rpc("research_pm_positioning", {
        _user_id: uid,
        _scenarios: null,
      });
      if (error) throw error;
      return ((data as unknown as PositioningRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface PmHouseViewRow extends Row {
  subject_label?: string;
  thesis_direction?: string;
  thesis_edge_id?: string;
  signal_label?: string;
  geo_compat?: number;
}
export function usePmHouseView() {
  return useQuery({
    queryKey: ["research_pm_house_view"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const uid = await ownUid();
      const { data, error } = await supabase.rpc("research_pm_house_view", { _user_id: uid });
      if (error) throw error;
      return ((data as unknown as PmHouseViewRow[]) ?? []).filter(Boolean);
    },
  });
}

// Boolean drill gate. Admins always true (server). Used to hide/disable out-of-mandate
// drill items — NOT the top-level tab.
export function usePmInScope(params: { fundId?: string; range?: string; portfolio?: number } | null) {
  return useQuery({
    queryKey: ["research_pm_in_scope", params],
    enabled: !!params,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const uid = await ownUid();
      const args: Row = { _user_id: uid };
      if (params?.fundId) args._fund_id = params.fundId;
      if (params?.range) args._range = params.range;
      if (params?.portfolio != null) args._portfolio = params.portfolio;
      const { data, error } = await supabase.rpc("research_pm_in_scope", args as any);
      if (error) throw error;
      return Boolean(data);
    },
  });
}
